import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CompactEmptyState } from "./SectionLayout";
import { currencyLabel, formatMoney, moneyStep, parseAsins } from "./sponsored-brands-format";
import type {
  CategoryTargetRow,
  RecommendationType,
  SponsoredBrandsMoneyCapabilities,
} from "./sponsored-brands-model";

export function ProductTargeting({
  money,
  input,
  onInputChange,
  asins,
  onAsinsChange,
  advertisedAsins,
  categories,
  onCategoriesChange,
  categorySuggestions,
  productSuggestions,
  onLoadRecommendations,
  recommendationsLoading,
  bid,
  onBidChange,
  negativeInput,
  onNegativeInputChange,
  negativeAsins,
  onNegativeAsinsChange,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  input: string;
  onInputChange: (value: string) => void;
  asins: string[];
  onAsinsChange: (value: string[]) => void;
  advertisedAsins: string[];
  categories: CategoryTargetRow[];
  onCategoriesChange: (value: CategoryTargetRow[]) => void;
  categorySuggestions: CategoryTargetRow[];
  productSuggestions: string[];
  onLoadRecommendations: (type: RecommendationType) => void;
  recommendationsLoading: boolean;
  bid: string;
  onBidChange: (value: string) => void;
  negativeInput: string;
  onNegativeInputChange: (value: string) => void;
  negativeAsins: string[];
  onNegativeAsinsChange: (value: string[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h3 className="mb-3 font-semibold">{t("sb.targeting.product")}</h3>
        <div className="mb-4">
          <CustomBidControl money={money} value={bid} onChange={onBidChange} />
        </div>
        <div
          data-slot="sb-product-targeting-workspace"
          className="grid min-h-[340px] grid-cols-2 overflow-hidden rounded-lg border border-border"
        >
          <Tabs defaultValue="categories" className="min-w-0 p-4">
            <TabsList>
              <TabsTrigger value="categories">{t("sb.productTargeting.categories")}</TabsTrigger>
              <TabsTrigger value="individual">{t("sb.productTargeting.individual")}</TabsTrigger>
            </TabsList>
            <TabsContent value="categories" className="pt-4">
              <div className="flex flex-col gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="self-start"
                  disabled={advertisedAsins.length === 0 || recommendationsLoading}
                  onClick={() => onLoadRecommendations("category")}
                >
                  {t("sb.productTargeting.loadCategories")}
                </Button>
                {advertisedAsins.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("sb.blockers.categorySuggestions")}
                  </p>
                ) : null}
                {categorySuggestions.map((category) => (
                  <Field key={category.id} orientation="horizontal">
                    <Checkbox
                      checked={categories.some((item) => item.id === category.id)}
                      onCheckedChange={(checked) =>
                        onCategoriesChange(
                          checked === true
                            ? [...categories.filter((item) => item.id !== category.id), category]
                            : categories.filter((item) => item.id !== category.id),
                        )
                      }
                    />
                    <FieldLabel>{category.name}</FieldLabel>
                  </Field>
                ))}
              </div>
            </TabsContent>
            <TabsContent value="individual" className="pt-4">
              <Tabs defaultValue="search">
                <TabsList>
                  <TabsTrigger value="search">{t("sb.products.search")}</TabsTrigger>
                  <TabsTrigger value="enter">{t("sb.products.enter")}</TabsTrigger>
                </TabsList>
                <TabsContent value="search" className="pt-3">
                  <div className="flex flex-col gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="self-start"
                      disabled={advertisedAsins.length === 0 || recommendationsLoading}
                      onClick={() => onLoadRecommendations("product")}
                    >
                      {t("sb.productTargeting.loadProducts")}
                    </Button>
                    {productSuggestions.map((asin) => (
                      <Field key={asin} orientation="horizontal">
                        <Checkbox
                          checked={asins.includes(asin)}
                          onCheckedChange={(checked) =>
                            onAsinsChange(
                              checked === true
                                ? [...new Set([...asins, asin])]
                                : asins.filter((item) => item !== asin),
                            )
                          }
                        />
                        <FieldLabel className="font-mono">{asin}</FieldLabel>
                      </Field>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="enter" className="flex flex-col gap-3 pt-3">
                  <Textarea
                    placeholder={t("sb.products.asinPlaceholder")}
                    value={input}
                    onChange={(event) => onInputChange(event.target.value)}
                  />
                  <Button
                    type="button"
                    className="self-start"
                    disabled={!parseAsins(input).length}
                    onClick={() => {
                      onAsinsChange([...new Set([...asins, ...parseAsins(input)])]);
                      onInputChange("");
                    }}
                  >
                    {t("sb.actions.add")}
                  </Button>
                </TabsContent>
              </Tabs>
            </TabsContent>
          </Tabs>
          <div className="min-w-0 border-l border-border p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-medium">
                {t("sb.common.added", { count: asins.length + categories.length })}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!asins.length && !categories.length}
                onClick={() => {
                  onAsinsChange([]);
                  onCategoriesChange([]);
                }}
              >
                {t("sb.common.removeAll")}
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("sb.productTargeting.categoryAndProduct")}</TableHead>
                  <TableHead>{t("sb.productTargeting.bid")}</TableHead>
                  <TableHead>{t("sb.common.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {asins.length || categories.length ? (
                  <>
                    {categories.map((category) => (
                      <TableRow key={category.id}>
                        <TableCell>{category.name}</TableCell>
                        <TableCell>{formatMoney(bid, money.currencyCode)}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("sb.common.remove", { name: category.name })}
                            onClick={() =>
                              onCategoriesChange(
                                categories.filter((item) => item.id !== category.id),
                              )
                            }
                          >
                            <IconTrash />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {asins.map((asin) => (
                      <TableRow key={asin}>
                        <TableCell className="font-mono">{asin}</TableCell>
                        <TableCell>{formatMoney(bid, money.currencyCode)}</TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t("sb.common.remove", { name: asin })}
                            onClick={() => onAsinsChange(asins.filter((item) => item !== asin))}
                          >
                            <IconTrash />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <CompactEmptyState />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      <div>
        <h3 className="mb-3 font-semibold">{t("sb.productTargeting.negative")}</h3>
        <div
          data-slot="sb-negative-product-workspace"
          className="grid min-h-[340px] grid-cols-2 overflow-hidden rounded-lg border border-border"
        >
          <Tabs defaultValue="products" className="min-w-0 p-4">
            <TabsList>
              <TabsTrigger value="products">{t("sb.productTargeting.excludeProducts")}</TabsTrigger>
              <TabsTrigger value="brands" disabled title={t("sb.blockers.negativeBrandContract")}>
                {t("sb.productTargeting.excludeBrands")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="products" className="pt-3">
              <div className="flex flex-col gap-3">
                <Textarea
                  value={negativeInput}
                  onChange={(event) => onNegativeInputChange(event.target.value)}
                  placeholder={t("sb.products.asinPlaceholder")}
                />
                <Button
                  type="button"
                  className="self-start"
                  disabled={!parseAsins(negativeInput).length}
                  onClick={() => {
                    onNegativeAsinsChange([
                      ...new Set([...negativeAsins, ...parseAsins(negativeInput)]),
                    ]);
                    onNegativeInputChange("");
                  }}
                >
                  {t("sb.actions.add")}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
          <div className="min-w-0 border-l border-border p-4">
            <div className="mb-2 flex items-center justify-between">
              <p className="font-medium">{t("sb.common.added", { count: negativeAsins.length })}</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={!negativeAsins.length}
                onClick={() => onNegativeAsinsChange([])}
              >
                {t("sb.common.removeAll")}
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("sb.productTargeting.product")}</TableHead>
                  <TableHead>{t("sb.common.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {negativeAsins.length ? (
                  negativeAsins.map((asin) => (
                    <TableRow key={asin}>
                      <TableCell className="font-mono">{asin}</TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label={t("sb.common.remove", { name: asin })}
                          onClick={() =>
                            onNegativeAsinsChange(negativeAsins.filter((item) => item !== asin))
                          }
                        >
                          <IconTrash />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <CompactEmptyState />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomBidControl({
  money,
  value,
  onChange,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <Field className="max-w-sm">
      <FieldLabel>{t("sb.keyword.bid")}</FieldLabel>
      <div className="flex gap-2">
        <Select items={[{ value: "custom", label: t("sb.keyword.customBid") }]} value="custom">
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">{t("sb.keyword.customBid")}</SelectItem>
          </SelectContent>
        </Select>
        <InputGroup className="w-32">
          <InputGroupAddon>
            <InputGroupText>{currencyLabel(money.currencyCode)}</InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            type="number"
            min={money.bid.min}
            max={money.bid.max}
            step={moneyStep(money.bid.decimals)}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
        </InputGroup>
      </div>
    </Field>
  );
}
